/**
 * Helper function allowing prefixing a readonly string[] with a string while preserving the exact type.
 */
type PrefixedArray<T extends readonly string[], P extends string> = { [K in keyof T]: `${P}${T[K]}` };
function prefixArray<T extends readonly string[], P extends string>(array: T, prefix: P): PrefixedArray<T, P> {
    return array.map((value) => `${prefix}${value}`) as PrefixedArray<T, P>;
}

export const CHANNELS = ["retail", "wholesale"] as const;
export type Channel = (typeof CHANNELS)[number];

export const COUNTRIES = ["cz", "sk", "pl", "hu", "ro", "de", "at", "fr", "it"] as const;
export type Country = (typeof COUNTRIES)[number];

// MPT "locale" is the same country, except "cz" is "cs"
export type Locale = "cs" | Exclude<Country, "cz">;
export const LOCALES = ["cs", ...COUNTRIES.filter((country) => country !== "cz")] as [Locale, ...Locale[]];

export const LANGUAGES = ["cs", "sk", "pl", "hu", "ro", "de", "fr", "it"] as const;
export type Language = (typeof LANGUAGES)[number];

export const WHOLESALE_COUNTRIES = ["cz", "sk", "pl", "hu"] as const;
export type WholesaleCountry = (typeof WHOLESALE_COUNTRIES)[number];

export const MARKETS = [...COUNTRIES, ...prefixArray(WHOLESALE_COUNTRIES, "wh")] as const;
export type Market = (typeof MARKETS)[number];

export const ADMIN_SERVERS = [...COUNTRIES, "wh"] as const;
export type AdminServer = (typeof ADMIN_SERVERS)[number];

export const LOCALE_TO_COUNTRY = Object.fromEntries(
    COUNTRIES.map((country) => {
        const locale = country === "cz" ? "cs" : country;
        return [locale, country.toUpperCase()];
    }),
) as Record<Locale, Uppercase<Country>>;

export const COUNTRY_TO_LANGUAGE: Record<Country, Language> = {
    cz: "cs",
    sk: "sk",
    pl: "pl",
    hu: "hu",
    ro: "ro",
    de: "de",
    at: "de",
    fr: "fr",
    it: "it",
};

export const FLAGS: Record<Country, string> = {
    cz: "🇨🇿",
    sk: "🇸🇰",
    pl: "🇵🇱",
    hu: "🇭🇺",
    ro: "🇷🇴",
    de: "🇩🇪",
    at: "🇦🇹",
    fr: "🇫🇷",
    it: "🇮🇹",
};

export function sortedCountries(countries: Country[]): Country[] {
    return COUNTRIES.filter((country) => countries.includes(country));
}

export function marketToChannelAndCountry(market: Market): [Channel, Country] {
    return [market.startsWith("wh") ? "wholesale" : "retail", market.slice(-2) as Country];
}

export function channelAndCountryToMarket(channel: Channel, country: Country): Market {
    if (channel === "wholesale" && !WHOLESALE_COUNTRIES.includes(country as WholesaleCountry)) {
        throw new Error(`country ${country} is not wholesale`);
    }
    return `${channel === "wholesale" ? "wh" : ""}${country}` as Market;
}

export function sortByCountry<T>(values: T[], key?: (value: T) => Country): T[] {
    if (typeof key === "undefined") {
        key = (value) => (value as { country: Country }).country;
    }
    return [...values].sort((a, b) => COUNTRIES.indexOf(key(a)) - COUNTRIES.indexOf(key(b)));
}
